import { Action, combineReducers, configureStore } from '@reduxjs/toolkit'
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux'
import foldersReducer from './slices/foldersSlice'
import filesReducer from './slices/filesSlice'
import uiReducer from './slices/uiSlice'
import searchReducer from './slices/searchSlice'
import permissionsReducer from './slices/permissionsSlice'
import filePermissionsReducer from './slices/filePermissionsSlice'
import groupsReducer from './slices/groupsSlice'
import uploadsReducer from './slices/uploadsSlice'
import fileProcessingReducer from './slices/fileProcessingSlice'

// Global action that resets every slice back to its initialState.
// Dispatched on logout so no user-scoped data leaks into the next session.
export const LOGOUT_ACTION_TYPE = 'auth/logout' as const
export const logoutAction = (): Action<typeof LOGOUT_ACTION_TYPE> => ({
  type: LOGOUT_ACTION_TYPE,
})

const appReducer = combineReducers({
  folders: foldersReducer,
  files: filesReducer,
  ui: uiReducer,
  search: searchReducer,
  permissions: permissionsReducer,
  filePermissions: filePermissionsReducer,
  groups: groupsReducer,
  uploads: uploadsReducer,
  fileProcessing: fileProcessingReducer,
})

const rootReducer: typeof appReducer = (state, action) => {
  if (action.type === LOGOUT_ACTION_TYPE) {
    return appReducer(undefined, action)
  }
  return appReducer(state, action)
}

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore these field paths in all actions
        ignoredActionPaths: ['payload.timestamp'],
        // No longer need to ignore ui.selectedFiles since it's now an array
      },
    }),
})

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch

// Typed hooks for better TypeScript support
export const useAppDispatch = () => useDispatch<AppDispatch>()
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector
